import { SiteDomain, DomainMapping, SiteData, FormattedDnsInstruction } from '../types';
import { realtimeDbService as dbService } from './realtimeDbService';
import { normalizeDomain, validateDomainInput } from '../utils/security';
import { StandardDomainProvider, StandardSslProvider, StandardCdnProvider, DnsCheckResult } from './providerInterfaces';
import { SiteResolverService } from './siteResolverService';
import { apiFetch, ApiError } from '../utils/apiClient';

export class DomainService {
  private static domainProvider = new StandardDomainProvider();
  private static sslProvider = new StandardSslProvider();
  private static cdnProvider = new StandardCdnProvider();

  /**
   * Get formatted DNS record instructions
   */
  public static getDnsInstructions(customDomain: string, subdomain: string, token?: string) {
    const clean = normalizeDomain(customDomain);
    const isApex = !clean.startsWith('www.') && clean.split('.').length === 2;

    return {
      aRecord: {
        type: 'A',
        name: isApex ? '@' : clean,
        value: '',
        description: 'Firebase Custom Domain API tarafından dinamik olarak temin edilir.',
      },
      cnameRecord: {
        type: 'CNAME',
        name: 'www',
        value: '',
        description: 'Firebase Custom Domain API tarafından dinamik olarak temin edilir.',
      },
      txtRecord: {
        type: 'TXT',
        name: '_adiosoft-challenge',
        value: token || '',
        description: 'Firebase alan adı mülkiyet doğrulama güvenlik belirteci.',
      },
    };
  }

  /**
   * Check DNS propagation and records for custom domain
   */
  public static async verifyCustomDomain(domainName: string): Promise<{ success: boolean; message: string }> {
    const cleanDomain = normalizeDomain(domainName);
    const dnsResult = await this.domainProvider.checkDnsRecords(cleanDomain);

    if (!dnsResult.success) {
      return {
        success: false,
        message: dnsResult.error || 'DNS kayıtları henüz yönlendirilmemiş veya geçersiz format.',
      };
    }

    return {
      success: true,
      message: 'DNS kayıtları başarıyla doğrulandı ve Let\'s Encrypt SSL sertifikası tanımlandı.',
    };
  }

  public static async verifyDnsRecords(domainName: string, siteId?: string): Promise<{
    domain: string;
    isPropagated: boolean;
    firebaseStatus?: {
      hostState?: string;
      ownershipState?: string;
      sslStatus?: string;
      status: string;
      issues?: Array<{ code: string; message: string; severity: string }>;
    };
    dnsInstructions?: FormattedDnsInstruction[];
    error?: string;
  }> {
    const cleanDomain = normalizeDomain(domainName);
    try {
      const res = await fetch('/api/domains/check-dns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain: cleanDomain,
          siteId,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        return {
          domain: cleanDomain,
          isPropagated: !!data.isPropagated,
          firebaseStatus: data.firebaseStatus,
          dnsInstructions: data.dnsInstructions || [],
          error: data.firebaseStatus?.issues?.[0]?.message || data.error || (data.isPropagated ? undefined : 'DNS yönlendirmesi henüz doğrulanmadı.'),
        };
      }
      const errData = await res.json().catch(() => ({}));
      return {
        domain: cleanDomain,
        isPropagated: false,
        error: errData.error || 'DNS kontrol servisine erişilemedi.',
      };
    } catch (apiErr: any) {
      console.warn('Backend DNS check API call failed:', apiErr);
      return {
        domain: cleanDomain,
        isPropagated: false,
        error: apiErr?.message || 'DNS kontrolü gerçekleştirilemedi.',
      };
    }
  }

  /**
   * Adds custom domain via Firebase Custom Domain API
   */
  public static async addDomain(siteId: string, domainName: string, ownerId?: string): Promise<SiteDomain> {
    const validation = validateDomainInput(domainName);
    if (!validation.isValid) {
      throw new Error(validation.error || 'Lütfen geçerli bir alan adı giriniz (örn: sirketiniz.com).');
    }
    const cleanDomain = validation.normalized;

    try {
      const data = await apiFetch<{
        success: boolean;
        domain?: SiteDomain | string;
        siteDomain?: SiteDomain;
        dnsInstructions?: FormattedDnsInstruction[];
        requiredDnsUpdates?: FormattedDnsInstruction[];
        dnsRecords?: FormattedDnsInstruction[];
        error?: string;
        code?: string;
      }>('/api/domains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          websiteId: siteId,
          siteId,
          tenantId: ownerId,
          ownerId,
          domain: cleanDomain,
        }),
      });

      if (!data || !data.success) {
        throw new Error(data?.error || 'Alan adı bağlantısı oluşturulamadı. Lütfen tekrar deneyin.');
      }

      const instructions = data.dnsInstructions || data.requiredDnsUpdates || data.dnsRecords || [];
      const baseDomain: SiteDomain =
        typeof data.siteDomain === 'object' && data.siteDomain !== null
          ? data.siteDomain
          : typeof data.domain === 'object' && data.domain !== null
          ? (data.domain as SiteDomain)
          : {
              id: `dom_${Date.now().toString(36)}`,
              siteId,
              domain: cleanDomain,
              isPrimary: true,
              status: 'PENDING_DNS',
              sslStatus: 'PENDING',
              createdAt: Date.now(),
              updatedAt: Date.now(),
            };

      baseDomain.dnsInstructions = instructions;
      return baseDomain;
    } catch (err: any) {
      if (err instanceof ApiError) {
        if (err.isHtmlFallback) {
          throw new Error('API yapılandırma hatası: Sunucu bağlantısı sağlanamadı. Lütfen sistem durumunu kontrol ediniz.');
        }
        throw new Error(err.message || 'Alan adı bağlantısı oluşturulamadı. Lütfen tekrar deneyin.');
      }
      throw new Error(err?.message || 'Alan adı bağlantısı oluşturulamadı. Lütfen tekrar deneyin.');
    }
  }

  /**
   * Verifies DNS and re-syncs state with Firebase Custom Domain API
   */
  public static async verifyAndActivateDomain(
    siteId: string,
    domainId: string,
    userId = 'system'
  ): Promise<SiteDomain> {
    const site = await dbService.getSiteById(siteId);
    if (!site) throw new Error('Site bulunamadı.');

    const domains = site.domains || [];
    const domIndex = domains.findIndex((d) => d.id === domainId);
    if (domIndex === -1) throw new Error('Domain kaydı bulunamadı.');

    const domain = domains[domIndex];
    domain.status = 'VERIFYING';
    await dbService.updateSite(site);

    const check = await this.verifyDnsRecords(domain.domain || '', siteId);

    const isConnected = check.firebaseStatus?.status === 'CONNECTED' || check.firebaseStatus?.hostState === 'HOST_ACTIVE';
    const isSslActive = check.firebaseStatus?.sslStatus === 'SSL_ACTIVE';

    domain.status = isConnected ? 'ACTIVE' : 'PENDING_DNS';
    domain.sslStatus = isSslActive ? 'ACTIVE' : 'PENDING';
    domain.dnsInstructions = check.dnsInstructions || domain.dnsInstructions || [];
    domain.hostState = check.firebaseStatus?.hostState;
    domain.ownershipState = check.firebaseStatus?.ownershipState;

    if (isConnected) {
      domain.verifiedAt = Date.now();
      site.domainStatus = 'ACTIVE';
      site.sslStatus = isSslActive ? 'ACTIVE' : 'PENDING';
      SiteResolverService.invalidateCache(domain.domain);
      await this.cdnProvider.purgeSiteCache(siteId, [domain.domain]);

      await dbService.createAuditLog(
        siteId,
        userId,
        'DOMAIN_VERIFIED',
        `Alan adı ve SSL sertifikası başarıyla doğrulandı ve aktive edildi: ${domain.domain}`
      );
    } else {
      site.domainStatus = 'PENDING_DNS';
      site.sslStatus = 'PENDING';
    }

    site.domains[domIndex] = domain;
    await dbService.updateSite(site);

    return domain;
  }

  /**
   * Sets the primary (canonical) domain for a website.
   * Directs search engine indexers and visitors to this domain as the canonical host.
   */
  public static async setPrimaryDomain(
    siteId: string,
    domainId: string,
    userId: string = 'system'
  ): Promise<SiteData> {
    const site = await dbService.getSiteById(siteId);
    if (!site) throw new Error('Site bulunamadı.');

    const domains = site.domains || [];
    let selectedDomainName = '';
    const updatedDomains = domains.map((d) => {
      const isTarget = d.id === domainId;
      if (isTarget) selectedDomainName = d.domain;
      return {
        ...d,
        isPrimary: isTarget,
      };
    });

    site.domains = updatedDomains;
    if (selectedDomainName) {
      site.customDomain = selectedDomainName;
    }

    const updatedSite = await dbService.updateSite(site);

    for (const d of updatedDomains) {
      const mapping = await dbService.getDomainMapping(d.domain);
      if (mapping) {
        mapping.isPrimary = d.isPrimary;
        mapping.isCanonical = d.isPrimary;
        await dbService.saveDomainMapping(mapping);
        SiteResolverService.invalidateCache(d.domain);
      }
    }

    await dbService.createAuditLog(
      siteId,
      userId,
      'DOMAIN_PRIMARY_SET',
      `Birincil (Canonical) alan adı güncellendi: ${selectedDomainName}`
    );

    return updatedSite;
  }

  /**
   * Detaches domain safely and cleans up Firebase Custom Domain on backend
   */
  public static async removeDomain(siteId: string, domainName: string, userId = 'system'): Promise<void> {
    const clean = normalizeDomain(domainName);

    // Call backend unbind endpoint to remove custom domain from Firebase Hosting
    try {
      await apiFetch('/api/domains/unbind', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId, domain: clean, ownerId: userId }),
      });
    } catch (unbindErr) {
      console.warn('[DomainService] Backend unbind notice:', unbindErr);
    }

    await dbService.removeDomainMapping(clean);
    SiteResolverService.invalidateCache(clean);

    const site = await dbService.getSiteById(siteId);
    if (site) {
      site.customDomain = undefined;
      site.domainStatus = undefined;
      site.sslStatus = undefined;
      site.domains = (site.domains || []).filter((d) => (d.domain || '').toLowerCase() !== clean);
      await dbService.updateSite(site);

      await dbService.createAuditLog(
        siteId,
        userId,
        'SITE_UPDATED',
        `Özel alan adı kaldırıldı: ${clean}`
      );
    }
  }
}
